// src/pages/CustomerGallery.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCustomerById } from '../services/customerAuth';
import { getPhotosByAlbumId } from '../services/photoService';
import PhotoGallery from '../components/PhotoGallery';
import Header from '../components/Header';

function CustomerGallery() {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const { customer, logout, isLoading: authLoading } = useAuth();
    const [photos, setPhotos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const handleModalNavigation = () => {
        navigate('/');
    };

    const loadPhotos = useCallback(async () => {
        setIsLoading(true);
        setError('');

        try {
            const customerData = getCustomerById(customerId);
            if (!customerData) {
                throw new Error('고객 정보를 찾을 수 없습니다.');
            }

            const photoData = await getPhotosByAlbumId(customerData.albumId);
            setPhotos(photoData);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        // Wait for auth to finish loading before checking authentication
        if (authLoading) {
            return;
        }

        // Verify customer is authenticated and matches the route
        if (!customer || customer.id !== customerId) {
            navigate('/');
            return;
        }

        loadPhotos();
    }, [customer, customerId, navigate, loadPhotos, authLoading]);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Show loading state while auth is being restored from localStorage
    if (authLoading) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    if (!customer) {
        return null;
    }

    return (
        <div className="customer-gallery-page">
            {/* Standard Header */}
            <Header
                openReviewsModal={handleModalNavigation}
                openPortfolioModal={handleModalNavigation}
            />

            {/* User Context Bar */}
            <div className="user-context-bar">
                <div className="container">
                    <div className="user-context-content">
                        <span className="customer-name">{customer.name}님</span>
                        <button onClick={handleLogout} className="logout-button">
                            로그아웃
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <main className="gallery-main">
                <div className="container">
                    <div className="gallery-title-section">
                        <h1>시공 사진 갤러리</h1>
                        <p>맑은뷰연구소가 작업한 결과를 확인하세요</p>
                    </div>

                    {isLoading && (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>사진을 불러오는 중...</p>
                        </div>
                    )}

                    {error && (
                        <div className="error-container">
                            <p className="error-message">{error}</p>
                            <button onClick={loadPhotos} className="cta-button">
                                다시 시도
                            </button>
                        </div>
                    )}

                    {!isLoading && !error && (() => {
                        // Group photos by date
                        const photosByDate = photos.reduce((acc, photo) => {
                            if (!acc[photo.date]) {
                                acc[photo.date] = [];
                            }
                            acc[photo.date].push(photo);
                            return acc;
                        }, {});

                        // Sort dates in descending order (newest first)
                        const sortedDates = Object.keys(photosByDate).sort((a, b) =>
                            new Date(b) - new Date(a)
                        );

                        const totalPhotos = photos.length;

                        return (
                            <>
                                <div className="gallery-info">
                                    <p>총 {totalPhotos}장의 사진</p>
                                </div>
                                {sortedDates.map(date => (
                                    <div key={date} className="photo-date-group">
                                        <h3 className="service-date">{date}</h3>
                                        <PhotoGallery photos={photosByDate[date]} />
                                    </div>
                                ))}
                            </>
                        );
                    })()}
                </div>
            </main>

            {/* Footer */}
            <footer className="gallery-footer">
                <div className="container">
                    <Link to="/" className="back-home-link">
                        ← 메인 페이지로 돌아가기
                    </Link>
                    <p>© 2025 맑은뷰연구소. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
}

export default CustomerGallery;
